import type { Message, NotificationLevel } from '@spawnwire/shared';
import { displayNameIn, isMuted, privateChannelName, useStore } from '../store/store';
import { desktop } from './desktop';
import { channelPath, getCurrentChannel, navigate } from './nav';
import { native } from './native';
import { plainText } from './markdown';
import { isActive } from './viewing';

let audio: AudioContext | null = null;

/** A short two-tone chime synthesised with Web Audio (no audio files to load). */
export function playChime(kind: 'message' | 'join' | 'leave' | 'ring' = 'message'): void {
  try {
    audio ??= new AudioContext();
    const ctx = audio;
    const notes = kind === 'join' ? [523, 784] : kind === 'leave' ? [784, 523] : kind === 'ring' ? [659, 880, 659] : [880, 1175];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.11;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.12, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.2);
    });
  } catch {
    /* audio unavailable */
  }
}

type SinkAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
let ringEl: SinkAudio | null = null;
let ringing = false;

function ringtoneElement(): SinkAudio {
  if (!ringEl) {
    ringEl = new Audio('/sounds/ringtone.wav') as SinkAudio;
    ringEl.loop = true;
    ringEl.preload = 'auto';
  }
  return ringEl;
}

/**
 * Browsers only let a page play sound after the person has clicked or typed in it. Touching the ringtone
 * element (and the chime's AudioContext) during the first gesture unlocks both for later, including Safari
 * and iOS, which track this per element. A ring that was blocked starts on that gesture too.
 */
export function unlockAudioOnGesture(): void {
  let unlocked = false;
  const unlock = () => {
    const el = ringtoneElement();
    if (ringing) void el.play().catch(() => {});
    else if (!unlocked) {
      unlocked = true;
      el.muted = true;
      void el
        .play()
        .then(() => {
          if (!ringing) {
            el.pause();
            el.currentTime = 0;
          }
          el.muted = false;
        })
        .catch(() => (el.muted = false));
    }
    try {
      audio ??= new AudioContext();
      void audio.resume();
    } catch {
      /* audio unavailable */
    }
  };
  for (const type of ['pointerdown', 'keydown'] as const) window.addEventListener(type, unlock, { capture: true, passive: true });
}

/** Loops the incoming-call ringtone until the returned function is called. Falls back to the chime if the browser blocks playback. */
export function startRingtone(): () => void {
  const el = ringtoneElement();
  const s = useStore.getState().me?.settings.audio;
  el.volume = Math.min(1, (s?.outputVolume ?? 100) / 100);
  if (s?.outputDeviceId && el.setSinkId) void el.setSinkId(s.outputDeviceId).catch(() => {});
  el.muted = false;
  el.currentTime = 0;
  ringing = true;
  let fallback: ReturnType<typeof setInterval> | undefined;
  el.play().catch(() => {
    if (!ringing) return;
    playChime('ring');
    fallback = setInterval(() => (el.paused ? playChime('ring') : clearInterval(fallback)), 2500);
  });
  return () => {
    ringing = false;
    el.pause();
    el.currentTime = 0;
    clearInterval(fallback);
  };
}

export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!notificationsSupported()) return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

function effectiveLevel(channelId: string, spaceId: string | null): NotificationLevel {
  const s = useStore.getState();
  const channelLevel = s.notificationSettings[channelId]?.level;
  if (channelLevel) return channelLevel;
  if (!spaceId) return 'all';
  return s.notificationSettings[spaceId]?.level ?? 'mentions';
}

export function mentionsMe(message: Message): boolean {
  const s = useStore.getState();
  const me = s.me;
  if (!me) return false;
  if (message.mentions.includes(me.id)) return true;
  if (message.spaceId) {
    const suppress = s.notificationSettings[message.spaceId]?.suppressEveryone;
    if (message.mentionEveryone && !suppress) return true;
    const myRoles = s.myMembers[message.spaceId]?.roleIds ?? [];
    if (message.mentionRoles.some((r) => myRoles.includes(r))) return true;
  }
  return false;
}

/**
 * Decides whether an incoming message deserves a sound or desktop notification. A published announcement
 * counts as a mention, so it notifies unless the channel or space is muted or set to nothing.
 */
export function maybeNotify(message: Message, opts: { announcement?: boolean } = {}): void {
  const s = useStore.getState();
  const me = s.me;
  if (!me || !message.author || message.author.id === me.id) return;
  if (message.type !== 'default' && message.type !== 'reply' && message.type !== 'call') return;
  if (s.relationships[message.author.id]?.type === 'blocked') return;
  if (me.status === 'dnd') return;
  const channel = s.channels[message.channelId];
  if (!channel) return;
  const rootId = channel.type === 'thread' && channel.parentId ? channel.parentId : channel.id;
  if (isMuted(s, rootId) || isMuted(s, channel.spaceId)) return;

  const level = effectiveLevel(rootId, channel.spaceId);
  const isPrivate = !channel.spaceId;
  const mention = isPrivate || !!opts.announcement || mentionsMe(message);
  if (level === 'nothing' || (level === 'mentions' && !mention)) return;
  if (isActive() && getCurrentChannel() === message.channelId) return;
  // In the Android app the server's push notification covers this, in front or not, so it isn't doubled here.
  if (native?.pushActive) return;

  if (me.settings.notificationSounds) playChime('message');
  if (native) return;
  // The Windows app has its own on/off switch (on by default); browsers need the account opt-in and permission.
  if (!desktop && !me.settings.desktopNotifications) return;
  const author = displayNameIn(s, message.author.id, channel.spaceId);
  const where = channel.spaceId ? `#${channel.name} · ${s.spaces[channel.spaceId]?.name ?? ''}` : privateChannelName(s, channel);
  const title = opts.announcement ? `📣 Announcement in ${where}` : channel.spaceId ? `${author} (${where})` : author;
  const body = message.type === 'call' ? 'Started a call' : plainText(message.content, s).slice(0, 180) || (message.attachments.length ? 'Sent an attachment' : '');
  if (desktop) {
    // A native Windows notification; clicking it brings the app forward on this conversation.
    desktop.notify({ id: message.id, tag: message.channelId, title, body, path: channelPath(channel) });
    return;
  }
  if (notificationsSupported() && Notification.permission === 'granted') {
    try {
      const n = new Notification(title, {
        body,
        tag: message.channelId,
        icon: message.author.avatarUrl ?? '/favicon.svg',
      });
      n.onclick = () => {
        window.focus();
        navigate(channelPath(channel));
        n.close();
      };
    } catch {
      /* some browsers only allow notifications from a service worker */
    }
  }
}
